import type RpcDataPagination from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort       from '@/core/services/Rpc/RpcDataSort';
import Core                   from '@/core/Core';
import Receipt                from '@/domains/Receipts/Receipt';
import ReceiptsDAO            from '@/domains/Receipts/ReceiptsDAO';
import Shipment               from '@/domains/Shipments/Shipment';
import ContextMenuItem        from '@/lib/components/VContextMenu/ContextMenuItem';


class ReceiptsService {

  // ============================================================
  // Чтение
  // ============================================================

  /**
   * Загрузить одну приёмку по ID.
   * Возвращает полностью гидрированный Receipt: с positions, complaints
   * и полным гидрированным Shipment.
   */

  static async get(id: number): Promise<Receipt | null> {
    const response = await ReceiptsDAO.get(id);

    return Core.utils.hydrate(response.data, Receipt.fromServer);
  }

  /**
   * Загрузить список приёмок с пагинацией для реестра через DataGridLoader.
   *
   * По умолчанию возвращает ТОНКИЕ объекты: только id, receiptDate, shipment={id}.
   * positions всегда пустой массив, shipment без полей продавца/покупателя/документа.
   *
   * Чтобы получить полные объекты — передайте requireNestedObjects: true,
   * но имейте в виду: это тяжёлый запрос. Для одной приёмки лучше get(id).
   */

  static async getListPaginated(
    params?: Parameters<typeof ReceiptsDAO.getList>[0] & {
      pagination?: RpcDataPagination;
      sort?: RpcDataSort;
    }
  ): Promise<{ data: Receipt[]; pagination: RpcDataPagination }> {
    const { pagination, sort, ...filters } = params ?? {};
    const response = await ReceiptsDAO.getList(filters, pagination, sort);

    return {
      data:       Core.utils.hydrateArray(response.data, Receipt.fromServer),
      pagination: response.meta.pagination,
    };
  }

  /**
   * Загрузить список приёмок без пагинации.
   * Для использования вне реестра (диалоги, автокомплиты, фоновая сверка).
   *
   * Возвращает ТОНКИЕ объекты — без positions, shipment={id}.
   * Если нужна полная гидрация — get(id) для каждой приёмки
   * или getListPaginated с requireNestedObjects: true.
   */

  static async getList(
    params?: Parameters<typeof ReceiptsDAO.getList>[0]
  ): Promise<Receipt[]> {
    return (await this.getListPaginated(params)).data;
  }

  // ============================================================
  // Запись
  // ============================================================

  /**
   * Создать или обновить приёмку.
   * Бэк сам роутит add/update по наличию receipt.id.
   */

  static async upsert(receipt: Receipt): Promise<boolean> {
    const response = await ReceiptsDAO.upsert(receipt);

    if (response.ok) {
      Core.showNotification.success(
        receipt.id ? 'Приёмка обновлена' : 'Приёмка создана'
      );
    }

    return response.ok;
  }

  /**
   * Удалить приёмку
   */

  static async delete(id: number, changeReasonText?: string): Promise<boolean> {
    const response = await ReceiptsDAO.delete(id, changeReasonText);

    if (response.ok) {
      Core.showNotification.success('Приёмка удалена');
    }

    return response.ok;
  }

  /**
   * Удалить приёмку с подтверждением и указанием причины.
   * Возвращает true если удаление произошло — вызывающий сам решает что делать
   * (обычно refresh лоадера регистра либо закрытие карточки).
   * При успехе помечает приёмку удалённой.
   */

  static async deleteWithConfirm(receipt: Receipt): Promise<boolean> {
    const { isApproved, reasonText } = await Core.dialogs.confirmWithReason('Удалить эту приёмку?');

    if (isApproved === true) {
      const isDeleted = await this.delete(receipt.id, reasonText);

      if (isDeleted === true) {
        receipt.isDeleted = true;
      }

      return isDeleted;
    } else {
      return false;
    }
  }

  /**
   * Отправить уведомление о ручной приёмке выбранным получателям.
   * Получателей выбирает вызывающий код — бэк сверяет их со своим списком
   * и отклоняет запрос, если среди них есть посторонние.
   * Отправка возможна только по ручной приёмке (externalId === null) — иначе бэк вернёт ошибку.
   */

  static async sendNotification(receipt: Receipt, receivers: { id: number }[]): Promise<boolean> {
    const response = await ReceiptsDAO.sendNotification(receipt.id, receivers);

    if (response.ok) {
      Core.showNotification.success('Уведомление отправлено');
    }

    return response.ok;
  }

  /**
   * Причина, по которой уведомление отправить нельзя. null — можно.
   * Приёмки из автоимпорта (externalId заполнен) бэк отклоняет,
   * поэтому пункт меню на них дизейблим.
   */

  static getReasonCannotSendNotification(receipt: Receipt): string | null {
    if (receipt.externalId === null) {
      return null;
    }
    else {
      return 'Уведомление можно отправить только по ручной приёмке';
    }
  }

  // ============================================================
  // Диалоги
  // ============================================================

  /**
   * Единая точка открытия карточки приёмки.
   * Карточка по условиям в init() сама разрулит что открывать:
   *   - передан Receipt | id          → редактирование / просмотр;
   *   - передан Shipment | shipmentId → создание приёмки на основе отгрузки;
   *   - ничего                        → пустая новая приёмка.
   */

  static openReceiptCard(receipt: Receipt | null, shipmentOrId?: Shipment | number | null): Promise<boolean> {
    return Core.dialogs.ReceiptCard.showAsPromise({ receipt, shipmentOrId });
  }

  /**
   * Диалог выбора получателей уведомления по ручной приёмке.
   * Первым аргументом showAsPromise идут пропсы диалога — отсюда обёртка { receipt }.
   */

  static openNotificationDialog(receipt: Receipt): Promise<boolean> {
    return Core.dialogs.ReceiptNotificationDialog.showAsPromise({ receipt });
  }

  // ============================================================
  // Контекстное меню
  // ============================================================

  static get contextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Открыть',
        'open_in_new',
        (receipt: Receipt) => { return this.openReceiptCard(receipt); },
        null,
        false,  // hidden
        true,   // dividerAfter
      ),
      new ContextMenuItem(
        'Отправить уведомление',
        'mail',
        (receipt: Receipt) => { this.openNotificationDialog(receipt); },
        (receipt: Receipt) => { return this.getReasonCannotSendNotification(receipt); },
        false,  // hidden
        true,   // dividerAfter
      ),
      new ContextMenuItem(
        'Удалить',
        'delete',
        (receipt: Receipt) => { return this.deleteWithConfirm(receipt); },
      ),
    ];
  }
}

export default ReceiptsService;